// SCR-STAFF-936 زبانه‌های فاز 2 (AP-ORG-03): گواهی‌ها و مدارک (US-ORG-033)، گارانتی و قرارداد (US-ORG-034)، نرخ هزینه (US-ORG-042)، نگهداری
// (برنامه، رخداد، رزرو زمان سرویس و ثبت انجام و کالیبراسیون — US-ORG-050، 051) و بهره‌وری (US-ORG-043).
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type CostRateBasis,
  type DeviceCertificateType,
  type DeviceCertificateView,
  type DeviceContractKind,
  type DeviceContractView,
  type DeviceCostRateView,
  type DeviceView,
  type MaintenanceKind,
  type MaintenanceOccurrenceStatus,
  type MaintenanceOccurrenceView,
  type MaintenancePlanView,
  type MaintenanceRecordView,
  type MaintenanceResult,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  CheckboxRHF,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  latinDigits,
  Money,
  MoneyFieldRHF,
  ReasonDialog,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  useDisplaySettings,
  zRequiredText,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { deviceKeys, percentOf, todayIn, zonedToUtcIso } from './deviceCommon';

export const CERTIFICATE_TYPES: readonly DeviceCertificateType[] = ['ImportLicense', 'Calibration', 'ElectricalSafety', 'RadiationProtection', 'Other'];
export const CONTRACT_KINDS: readonly DeviceContractKind[] = ['Warranty', 'ServiceContract', 'Comprehensive'];
const MAINTENANCE_KINDS: readonly MaintenanceKind[] = ['Service', 'Calibration'];
const RESULTS: readonly MaintenanceResult[] = ['Pass', 'PassWithNotes', 'Fail'];

export const maintenanceKeys = {
  device: (tenantId: string | undefined, id: string, part: string) => [...deviceKeys.device(tenantId, id), part] as const,
  dashboard: (tenantId: string | undefined) => ['organization', 'equipment', tenantId] as const,
};

/** رنگ وضعیت رخداد نگهداری. */
export function occurrenceTone(status: MaintenanceOccurrenceStatus): StatusTone {
  switch (status) {
    case 'Due':
      return 'pending';
    case 'Overdue':
      return 'error';
    case 'Booked':
      return 'booked';
    case 'Completed':
      return 'done';
    case 'Skipped':
      return 'cancelled';
    default:
      return 'inactive';
  }
}

/** رنگ اعتبار گواهی. */
export function validityTone(validity: DeviceCertificateView['validity']): StatusTone {
  return validity === 'Expired' ? 'error' : validity === 'Expiring' ? 'pending' : 'active';
}

/** بازه پیش‌فرض گزارش: ۳۰ روز گذشته تا اکنون. */
export function lastDays(days: number): { from: string; to: string } {
  const to = new Date();
  return { from: new Date(to.getTime() - days * 86_400_000).toISOString(), to: to.toISOString() };
}

function invalidateDevice(queryClient: ReturnType<typeof useQueryClient>, tenantId: string | undefined) {
  void queryClient.invalidateQueries({ queryKey: deviceKeys.devices(tenantId) });
  void queryClient.invalidateQueries({ queryKey: maintenanceKeys.dashboard(tenantId) });
}

function Rows({ rows, label }: { rows: [string, ReactNode][]; label: string }) {
  const { t } = useTranslation('org');
  return (
    <Table size="small" aria-label={label}>
      <TableBody>
        {rows.map(([key, value]) => (
          <TableRow key={key}>
            <TableCell component="th" scope="row" sx={{ width: 260 }}>
              {t(key)}
            </TableCell>
            <TableCell>{value}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ───────────── گواهی‌ها و مدارک (US-ORG-033) ─────────────

export function CertificatesTab({ device }: { device: DeviceView }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('org.device.manage');
  const [editing, setEditing] = useState<DeviceCertificateView | null | undefined>(undefined);
  const certificates = useQuery({
    queryKey: maintenanceKeys.device(session?.tenantId, device.id, 'certificates'),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/devices/{id}/certificates', { params: { path: { id: device.id } } })),
  });
  const columns: DataTableColumn<DeviceCertificateView>[] = [
    { id: 'type', headerKey: 'org:certificates.columns.type', cell: (row) => t(`org:certificate_type.${row.type}`) },
    { id: 'number', headerKey: 'org:certificates.columns.number', cell: (row) => <bdi dir="ltr">{row.number}</bdi> },
    { id: 'authority', headerKey: 'org:certificates.columns.authority', cell: (row) => row.authority },
    { id: 'issued', headerKey: 'org:certificates.columns.issued', cell: (row) => <JalaliDate value={row.issuedOn} /> },
    { id: 'expires', headerKey: 'org:certificates.columns.expires', cell: (row) => (row.expiresOn ? <JalaliDate value={row.expiresOn} /> : t('org:certificates.no_expiry')) },
    {
      id: 'validity',
      headerKey: 'org:certificates.columns.validity',
      cell: (row) => (
        <span data-testid={`certificate-validity-${row.number}`}>
          <StatusChip tone={validityTone(row.validity)} labelKey={`org:certificate_validity.${row.validity}`} />
        </span>
      ),
    },
    { id: 'blocking', headerKey: 'org:certificates.columns.blocking', cell: (row) => (row.isBlocking ? <Chip size="small" color="warning" label={t('org:certificates.blocking')} /> : '—') },
    {
      id: 'actions',
      headerKey: 'org:certificates.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setEditing(row)} disabled={!write.canWrite} data-testid={`certificate-edit-${row.number}`}>
          {t('org:certificates.renew')}
        </Button>
      ),
    },
  ];
  const warnings = (certificates.data?.items ?? []).filter((item) => item.validity !== 'Valid');
  return (
    <Stack spacing={2}>
      {warnings.map((item) => (
        <Alert key={item.id} severity={item.validity === 'Expired' ? 'error' : 'warning'} data-testid="certificate-warning">
          {t(item.validity === 'Expired' ? 'org:certificates.expired_warning' : 'org:certificates.expiring_warning', { type: t(`org:certificate_type.${item.type}`) })}
        </Alert>
      ))}
      <Stack direction="row" justifyContent="flex-end">
        <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setEditing(null)} disabled={!write.canWrite} data-testid="certificate-create">
          {t('org:certificates.add')}
        </Button>
      </Stack>
      <DataTable
        captionKey="org:certificates.caption"
        columns={columns}
        rows={certificates.data?.items}
        getRowId={(row) => row.id}
        isLoading={certificates.isPending}
        error={certificates.error}
        onRetry={() => void certificates.refetch()}
        emptyTitleKey="org:certificates.empty"
      />
      {editing !== undefined && <CertificateDialog device={device} certificate={editing} onClose={() => setEditing(undefined)} />}
    </Stack>
  );
}

const certificateSchema = z.object({
  type: z.enum(CERTIFICATE_TYPES as [DeviceCertificateType, ...DeviceCertificateType[]]),
  number: zRequiredText(100),
  authority: zRequiredText(200),
  issuedOn: z.string({ error: 'common:validation.required' }).min(1, { error: 'common:validation.required' }),
  expiresOn: z.string().nullable(),
  isBlocking: z.boolean(),
});
type CertificateForm = z.input<typeof certificateSchema>;

function CertificateDialog({ device, certificate, onClose }: { device: DeviceView; certificate: DeviceCertificateView | null; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const { control, handleSubmit, formState } = useForm<CertificateForm>({
    resolver: zodResolver(certificateSchema),
    defaultValues: {
      type: certificate?.type ?? 'Calibration',
      number: certificate?.number ?? '',
      authority: certificate?.authority ?? '',
      issuedOn: certificate?.issuedOn ?? todayIn(timeZone),
      expiresOn: certificate?.expiresOn ?? null,
      isBlocking: certificate?.isBlocking ?? false,
    },
  });
  const save = useMutation({
    mutationFn: (form: CertificateForm) => {
      const body = { type: form.type, number: form.number, authority: form.authority, issuedOn: form.issuedOn, expiresOn: form.expiresOn || null, fileId: certificate?.fileId ?? null, isBlocking: form.isBlocking };
      return certificate
        ? unwrap(tenantApi.PUT('/api/v1/organization/device-certificates/{id}', { params: { path: { id: certificate.id } }, body: { ...body, rowVersion: Number(certificate.rowVersion) } }))
        : unwrap(tenantApi.POST('/api/v1/organization/devices/{id}/certificates', { params: { path: { id: device.id } }, body: { ...body, rowVersion: null }, headers: { 'Idempotency-Key': idempotency.current() } }));
    },
    onSettled: (_data, error) => {
      if (!certificate) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: maintenanceKeys.device(session?.tenantId, device.id, 'certificates') });
      invalidateDevice(queryClient, session?.tenantId);
      onClose();
    },
  });
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-936-CERTIFICATE"
      titleKey={certificate ? 'org:certificates.edit_title' : 'org:certificates.create_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={save.error ?? undefined}
    >
      <SelectRHF control={control} name="type" labelKey="org:certificates.form.type" required fullWidth disabled={certificate !== null} options={CERTIFICATE_TYPES.map((value) => ({ value, labelKey: `org:certificate_type.${value}` }))} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF control={control} name="number" labelKey="org:certificates.form.number" required fullWidth slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'certificate-number' } }} />
        <TextFieldRHF control={control} name="authority" labelKey="org:certificates.form.authority" required fullWidth slotProps={{ htmlInput: { 'data-testid': 'certificate-authority' } }} />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <JalaliDatePickerRHF control={control} name="issuedOn" labelKey="org:certificates.form.issued" required />
        <JalaliDatePickerRHF control={control} name="expiresOn" labelKey="org:certificates.form.expires" helperKey="org:certificates.form.expires_help" />
      </Stack>
      <CheckboxRHF control={control} name="isBlocking" labelKey="org:certificates.form.blocking" helperKey="org:certificates.form.blocking_help" />
    </FormDialog>
  );
}

// ───────────── گارانتی و قرارداد خدمات (US-ORG-034) ─────────────

/** تأمین‌کنندگان خدمات فنی INV برای طرف قرارداد و انجام‌دهنده (فقط با inv.supplier.manage؛ بدون آن نام آزاد). */
function useTechnicalSuppliers() {
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const allowed = access.can('inv.supplier.manage');
  const suppliers = useQuery({
    queryKey: ['inventory', 'suppliers', session?.tenantId, 'technical'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/suppliers', { params: { query: { active: true, limit: 100 } } })),
    enabled: allowed,
  });
  return {
    allowed,
    options: (suppliers.data?.items ?? []).filter((item) => item.type === 'TechnicalServices' || item.type === 'Both').map((item) => ({ value: item.id, label: item.name })),
  };
}

export function ContractsTab({ device }: { device: DeviceView }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('org.device.manage');
  const [editing, setEditing] = useState<DeviceContractView | null | undefined>(undefined);
  const contracts = useQuery({
    queryKey: maintenanceKeys.device(session?.tenantId, device.id, 'contracts'),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/devices/{id}/contracts', { params: { path: { id: device.id } } })),
  });
  const columns: DataTableColumn<DeviceContractView>[] = [
    { id: 'kind', headerKey: 'org:contracts.columns.kind', cell: (row) => t(`org:contract_kind.${row.kind}`) },
    { id: 'party', headerKey: 'org:contracts.columns.party', cell: (row) => row.partyName },
    {
      id: 'period',
      headerKey: 'org:contracts.columns.period',
      cell: (row) => (
        <span>
          <JalaliDate value={row.startsOn} /> — <JalaliDate value={row.endsOn} />
        </span>
      ),
    },
    { id: 'coverage', headerKey: 'org:contracts.columns.coverage', cell: (row) => row.coverage ?? '—' },
    { id: 'amount', headerKey: 'org:contracts.columns.amount', cell: (row) => (row.amount ? <Money amountMinor={Number(row.amount.amount)} /> : '—') },
    { id: 'contact', headerKey: 'org:contracts.columns.contact', cell: (row) => row.contact ?? '—' },
    { id: 'active', headerKey: 'org:contracts.columns.active', cell: (row) => <StatusChip tone={row.isActive ? 'active' : 'inactive'} labelKey={row.isActive ? 'org:contracts.active' : 'org:contracts.inactive'} /> },
    {
      id: 'actions',
      headerKey: 'org:contracts.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setEditing(row)} disabled={!write.canWrite} data-testid={`contract-edit-${row.id}`}>
          {t('org:contracts.edit')}
        </Button>
      ),
    },
  ];
  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setEditing(null)} disabled={!write.canWrite} data-testid="contract-create">
          {t('org:contracts.add')}
        </Button>
      </Stack>
      <DataTable
        captionKey="org:contracts.caption"
        columns={columns}
        rows={contracts.data?.items}
        getRowId={(row) => row.id}
        isLoading={contracts.isPending}
        error={contracts.error}
        onRetry={() => void contracts.refetch()}
        emptyTitleKey="org:contracts.empty"
      />
      {editing !== undefined && <ContractDialog device={device} contract={editing} onClose={() => setEditing(undefined)} />}
    </Stack>
  );
}

const contractSchema = z.object({
  kind: z.enum(CONTRACT_KINDS as [DeviceContractKind, ...DeviceContractKind[]]),
  supplierId: z.string(),
  partyName: z.string().max(200),
  startsOn: z.string({ error: 'common:validation.required' }).min(1, { error: 'common:validation.required' }),
  endsOn: z.string({ error: 'common:validation.required' }).min(1, { error: 'common:validation.required' }),
  coverage: z.string().max(1000),
  amount: z.number().int().nonnegative().nullable(),
  contact: z.string().max(200),
});
type ContractForm = z.input<typeof contractSchema>;

function ContractDialog({ device, contract, onClose }: { device: DeviceView; contract: DeviceContractView | null; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const suppliers = useTechnicalSuppliers();
  const schema = contractSchema.superRefine((value, ctx) => {
    if (!value.supplierId && !value.partyName.trim()) {
      ctx.addIssue({ code: 'custom', path: ['partyName'], message: 'common:validation.required' });
    }
  });
  const { control, handleSubmit, formState } = useForm<ContractForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      kind: contract?.kind ?? 'Warranty',
      supplierId: contract?.supplierId ?? '',
      partyName: contract?.supplierId ? '' : (contract?.partyName ?? ''),
      startsOn: contract?.startsOn ?? todayIn(timeZone),
      endsOn: contract?.endsOn ?? todayIn(timeZone, 365),
      coverage: contract?.coverage ?? '',
      amount: contract?.amount ? Number(contract.amount.amount) : null,
      contact: contract?.contact ?? '',
    },
  });
  const save = useMutation({
    mutationFn: (form: ContractForm) => {
      const body = {
        kind: form.kind,
        supplierId: form.supplierId || null,
        partyName: form.supplierId ? null : form.partyName.trim(),
        startsOn: form.startsOn,
        endsOn: form.endsOn,
        coverage: form.coverage.trim() || null,
        amount: form.amount !== null && form.amount !== undefined ? { amount: form.amount, currency: 'IRR' } : null,
        contact: form.contact.trim() || null,
      };
      return contract
        ? unwrap(tenantApi.PUT('/api/v1/organization/device-contracts/{id}', { params: { path: { id: contract.id } }, body: { ...body, rowVersion: Number(contract.rowVersion) } }))
        : unwrap(tenantApi.POST('/api/v1/organization/devices/{id}/contracts', { params: { path: { id: device.id } }, body: { ...body, rowVersion: null }, headers: { 'Idempotency-Key': idempotency.current() } }));
    },
    onSettled: (_data, error) => {
      if (!contract) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: maintenanceKeys.device(session?.tenantId, device.id, 'contracts') });
      invalidateDevice(queryClient, session?.tenantId);
      onClose();
    },
  });
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-936-CONTRACT"
      titleKey={contract ? 'org:contracts.edit_title' : 'org:contracts.create_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={save.error ?? undefined}
    >
      <SelectRHF control={control} name="kind" labelKey="org:contracts.form.kind" required fullWidth options={CONTRACT_KINDS.map((value) => ({ value, labelKey: `org:contract_kind.${value}` }))} />
      {suppliers.allowed && (
        <SelectRHF control={control} name="supplierId" labelKey="org:contracts.form.supplier" helperKey="org:contracts.form.supplier_help" fullWidth options={[{ value: '', labelKey: 'org:contracts.form.supplier_none' }, ...suppliers.options]} />
      )}
      <TextFieldRHF control={control} name="partyName" labelKey="org:contracts.form.party" fullWidth slotProps={{ htmlInput: { 'data-testid': 'contract-party' } }} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <JalaliDatePickerRHF control={control} name="startsOn" labelKey="org:contracts.form.starts" required />
        <JalaliDatePickerRHF control={control} name="endsOn" labelKey="org:contracts.form.ends" required />
      </Stack>
      <TextFieldRHF control={control} name="coverage" labelKey="org:contracts.form.coverage" fullWidth multiline minRows={2} slotProps={{ htmlInput: { 'data-testid': 'contract-coverage' } }} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <MoneyFieldRHF control={control} name="amount" labelKey="org:contracts.form.amount" fullWidth />
        <TextFieldRHF control={control} name="contact" labelKey="org:contracts.form.contact" fullWidth slotProps={{ htmlInput: { 'data-testid': 'contract-contact' } }} />
      </Stack>
    </FormDialog>
  );
}

// ───────────── نرخ هزینه (US-ORG-042) ─────────────

/** مبناهای سازگار با واحد شمارنده (BR-ORG-043): Shot/Pulse ← PerUnit، Minute ← PerMinute یا PerUnit، Session/None ← PerSession. */
export function basesFor(unit: DeviceView['counterUnit']): CostRateBasis[] {
  switch (unit) {
    case 'Shot':
    case 'Pulse':
      return ['PerUnit', 'PerSession'];
    case 'Minute':
      return ['PerMinute', 'PerSession'];
    default:
      return ['PerSession'];
  }
}

export function CostRatesTab({ device }: { device: DeviceView }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('org.device.cost.manage');
  const [creating, setCreating] = useState(false);
  const rates = useQuery({
    queryKey: maintenanceKeys.device(session?.tenantId, device.id, 'cost-rates'),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/devices/{id}/cost-rates', { params: { path: { id: device.id } } })),
  });
  const columns: DataTableColumn<DeviceCostRateView>[] = [
    { id: 'effective', headerKey: 'org:cost_rates.columns.effective', cell: (row) => <JalaliDate value={row.effectiveFrom} /> },
    { id: 'basis', headerKey: 'org:cost_rates.columns.basis', cell: (row) => t(`org:cost_basis.${row.basis}`, { unit: t(`org:counter_unit.${row.unit}`) }) },
    { id: 'rate', headerKey: 'org:cost_rates.columns.rate', cell: (row) => <Money amountMinor={Number(row.rate.amount)} /> },
    { id: 'current', headerKey: 'org:cost_rates.columns.current', cell: (row) => (row.isCurrent ? <Chip size="small" color="success" label={t('org:cost_rates.current')} data-testid="cost-rate-current" /> : '—') },
  ];
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t('org:cost_rates.hint')}
      </Typography>
      <Stack direction="row" justifyContent="flex-end">
        <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setCreating(true)} disabled={!write.canWrite} data-testid="cost-rate-create">
          {t('org:cost_rates.add')}
        </Button>
      </Stack>
      <DataTable
        captionKey="org:cost_rates.caption"
        columns={columns}
        rows={rates.data?.items}
        getRowId={(row) => row.id}
        isLoading={rates.isPending}
        error={rates.error}
        onRetry={() => void rates.refetch()}
        emptyTitleKey="org:cost_rates.empty"
      />
      {creating && <CostRateDialog device={device} onClose={() => setCreating(false)} />}
    </Stack>
  );
}

const rateSchema = z.object({
  basis: z.string().min(1, { error: 'common:validation.required' }),
  rate: z.number({ error: 'common:validation.required' }).int().nonnegative(),
  effectiveFrom: z.string({ error: 'common:validation.required' }).min(1, { error: 'common:validation.required' }),
});
type RateForm = z.input<typeof rateSchema>;

function CostRateDialog({ device, onClose }: { device: DeviceView; onClose: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const bases = basesFor(device.counterUnit);
  const { control, handleSubmit, formState } = useForm<RateForm>({
    resolver: zodResolver(rateSchema),
    defaultValues: { basis: bases[0], rate: null as unknown as number, effectiveFrom: todayIn(timeZone) },
  });
  const save = useMutation({
    mutationFn: (form: RateForm) =>
      unwrap(
        tenantApi.POST('/api/v1/organization/devices/{id}/cost-rates', {
          params: { path: { id: device.id } },
          body: { basis: form.basis as CostRateBasis, rate: { amount: form.rate, currency: 'IRR' }, effectiveFrom: form.effectiveFrom },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: maintenanceKeys.device(session?.tenantId, device.id, 'cost-rates') });
      onClose();
    },
  });
  return (
    <FormDialog open screenId="SCR-STAFF-936-RATE" titleKey="org:cost_rates.create_title" onSubmit={() => void handleSubmit((form) => save.mutate(form))()} onClose={onClose} isDirty={formState.isDirty} isSubmitting={save.isPending} submitError={save.error ?? undefined}>
      <SelectRHF control={control} name="basis" labelKey="org:cost_rates.form.basis" required fullWidth options={bases.map((value) => ({ value, label: t(`org:cost_basis.${value}`, { unit: t(`org:counter_unit.${device.counterUnit}`) }) }))} />
      <MoneyFieldRHF control={control} name="rate" labelKey="org:cost_rates.form.rate" required fullWidth />
      <JalaliDatePickerRHF control={control} name="effectiveFrom" labelKey="org:cost_rates.form.effective" helperKey="org:cost_rates.form.effective_help" required />
    </FormDialog>
  );
}

// ───────────── نگهداری (US-ORG-050، 051) ─────────────

export function MaintenanceTab({ device }: { device: DeviceView }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('org.maintenance.manage');
  const queryClient = useQueryClient();
  const [planEditing, setPlanEditing] = useState<MaintenancePlanView | null | undefined>(undefined);
  const [booking, setBooking] = useState<MaintenanceOccurrenceView | null>(null);
  const [skipping, setSkipping] = useState<MaintenanceOccurrenceView | null>(null);
  const [recording, setRecording] = useState<MaintenanceOccurrenceView | null | undefined>(undefined);
  const key = maintenanceKeys.device(session?.tenantId, device.id, 'maintenance');
  const maintenance = useQuery({
    queryKey: key,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/devices/{id}/maintenance', { params: { path: { id: device.id } } })),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    invalidateDevice(queryClient, session?.tenantId);
  };
  const skip = useMutation({
    mutationFn: ({ occurrence, reason }: { occurrence: MaintenanceOccurrenceView; reason: string }) =>
      unwrap(tenantApi.POST('/api/v1/organization/maintenance-occurrences/{id}:skip', { params: { path: { id: occurrence.id } }, body: { reason } })),
    onSuccess: () => {
      refresh();
      setSkipping(null);
    },
  });
  const counterPlan = device.counterUnit !== 'None' && device.counterUnit !== 'Session';

  const planColumns: DataTableColumn<MaintenancePlanView>[] = [
    { id: 'name', headerKey: 'org:maintenance.plans.columns.name', cell: (row) => row.name },
    { id: 'kind', headerKey: 'org:maintenance.plans.columns.kind', cell: (row) => t(`org:maintenance_kind.${row.kind}`) },
    {
      id: 'interval',
      headerKey: 'org:maintenance.plans.columns.interval',
      cell: (row) =>
        row.trigger === 'Time'
          ? t('org:maintenance.every_days', { n: faDigits(Number(row.intervalDays)) })
          : t('org:maintenance.every_units', { n: faDigits(Number(row.intervalUnits)), unit: t(`org:counter_unit.${device.counterUnit}`) }),
    },
    { id: 'blocking', headerKey: 'org:maintenance.plans.columns.blocking', cell: (row) => (row.blockingAtDue ? t('org:maintenance.blocking') : '—') },
    { id: 'active', headerKey: 'org:maintenance.plans.columns.active', cell: (row) => <StatusChip tone={row.isActive ? 'active' : 'inactive'} /> },
    {
      id: 'actions',
      headerKey: 'org:maintenance.plans.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setPlanEditing(row)} disabled={!write.canWrite} data-testid={`plan-edit-${row.id}`}>
          {t('org:maintenance.plans.edit')}
        </Button>
      ),
    },
  ];
  const occurrenceColumns: DataTableColumn<MaintenanceOccurrenceView>[] = [
    { id: 'plan', headerKey: 'org:maintenance.occurrences.columns.plan', cell: (row) => row.planName },
    {
      id: 'due',
      headerKey: 'org:maintenance.occurrences.columns.due',
      cell: (row) =>
        row.trigger === 'Time' && row.dueOn ? (
          <JalaliDate value={row.dueOn} />
        ) : (
          <span data-testid={`occurrence-due-${row.id}`}>
            {t('org:maintenance.due_counter', { due: faDigits(Number(row.dueAtCounter ?? 0)), warn: faDigits(Number(row.warnAtCounter ?? 0)) })}
          </span>
        ),
    },
    { id: 'booked', headerKey: 'org:maintenance.occurrences.columns.booked', cell: (row) => (row.bookedFrom ? <JalaliDate value={row.bookedFrom} withTime /> : '—') },
    { id: 'parts', headerKey: 'org:maintenance.occurrences.columns.parts', cell: (row) => (Number(row.partsCost.amount) > 0 ? <Money amountMinor={Number(row.partsCost.amount)} /> : '—') },
    {
      id: 'status',
      headerKey: 'org:maintenance.occurrences.columns.status',
      cell: (row) => (
        <span data-testid={`occurrence-status-${row.id}`}>
          <StatusChip tone={occurrenceTone(row.status)} labelKey={`org:occurrence_status.${row.status}`} />
        </span>
      ),
    },
    {
      id: 'actions',
      headerKey: 'org:maintenance.occurrences.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {(row.status === 'Due' || row.status === 'Overdue') && (
            <>
              <Button size="small" onClick={() => setBooking(row)} disabled={!write.canWrite} data-testid={`occurrence-book-${row.id}`}>
                {t('org:maintenance.book')}
              </Button>
              <Button size="small" color="error" onClick={() => setSkipping(row)} disabled={!write.canWrite} data-testid={`occurrence-skip-${row.id}`}>
                {t('org:maintenance.skip')}
              </Button>
            </>
          )}
          {row.status === 'Booked' && (
            <Button size="small" variant="contained" onClick={() => setRecording(row)} disabled={!write.canWrite} data-testid={`occurrence-record-${row.id}`}>
              {t('org:maintenance.record')}
            </Button>
          )}
          {(row.status === 'Due' || row.status === 'Overdue' || row.status === 'Booked') && (
            <Typography variant="caption" color="text.secondary" component="span" sx={{ alignSelf: 'center' }}>
              {t('org:maintenance.reference')} <bdi dir="ltr" data-testid={`occurrence-ref-${row.id}`}>{row.id}</bdi>
            </Typography>
          )}
        </Stack>
      ),
    },
  ];
  const recordColumns: DataTableColumn<MaintenanceRecordView>[] = [
    { id: 'date', headerKey: 'org:maintenance.records.columns.date', cell: (row) => <JalaliDate value={row.performedOn} /> },
    { id: 'kind', headerKey: 'org:maintenance.records.columns.kind', cell: (row) => t(`org:maintenance_kind.${row.kind}`) },
    { id: 'performer', headerKey: 'org:maintenance.records.columns.performer', cell: (row) => (row.performer === 'Supplier' ? (row.supplierName ?? t('org:performer.Supplier')) : t('org:performer.Internal')) },
    { id: 'description', headerKey: 'org:maintenance.records.columns.description', cell: (row) => row.description },
    { id: 'result', headerKey: 'org:maintenance.records.columns.result', cell: (row) => <StatusChip tone={row.result === 'Fail' ? 'error' : 'done'} labelKey={`org:maintenance_result.${row.result}`} /> },
    {
      id: 'cost',
      headerKey: 'org:maintenance.records.columns.cost',
      cell: (row) => (
        <span data-testid={`record-cost-${row.id}`}>
          <Money amountMinor={Number(row.totalCost.amount)} />
        </span>
      ),
    },
  ];
  const data = maintenance.data;
  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" component="h3">
          {t('org:maintenance.plans.title')}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setRecording(null)} disabled={!write.canWrite} data-testid="maintenance-record-adhoc">
            {t('org:maintenance.record_adhoc')}
          </Button>
          <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setPlanEditing(null)} disabled={!write.canWrite} data-testid="plan-create">
            {t('org:maintenance.plans.add')}
          </Button>
        </Stack>
      </Stack>
      <DataTable captionKey="org:maintenance.plans.caption" columns={planColumns} rows={data?.plans} getRowId={(row) => row.id} isLoading={maintenance.isPending} error={maintenance.error} onRetry={() => void maintenance.refetch()} emptyTitleKey="org:maintenance.plans.empty" />
      <Typography variant="subtitle1" component="h3">
        {t('org:maintenance.occurrences.title')}
      </Typography>
      <DataTable captionKey="org:maintenance.occurrences.caption" columns={occurrenceColumns} rows={data?.occurrences} getRowId={(row) => row.id} isLoading={maintenance.isPending} emptyTitleKey="org:maintenance.occurrences.empty" />
      <Typography variant="subtitle1" component="h3">
        {t('org:maintenance.records.title')}
      </Typography>
      <DataTable captionKey="org:maintenance.records.caption" columns={recordColumns} rows={data?.records} getRowId={(row) => row.id} isLoading={maintenance.isPending} emptyTitleKey="org:maintenance.records.empty" />
      {planEditing !== undefined && <PlanDialog device={device} plan={planEditing} counterPlan={counterPlan} onClose={() => setPlanEditing(undefined)} onSaved={refresh} />}
      {booking && <BookDialog occurrence={booking} onClose={() => setBooking(null)} onSaved={refresh} />}
      {recording !== undefined && <RecordDialog device={device} occurrence={recording} onClose={() => setRecording(undefined)} onSaved={refresh} />}
      <ReasonDialog
        open={skipping !== null}
        titleKey="org:maintenance.skip_title"
        confirmKey="org:maintenance.skip"
        busy={skip.isPending}
        onConfirm={(reason) => skipping && skip.mutate({ occurrence: skipping, reason })}
        onCancel={() => setSkipping(null)}
        error={skip.error ?? undefined}
      />
    </Stack>
  );
}

const planSchema = z.object({
  name: zRequiredText(200),
  kind: z.enum(MAINTENANCE_KINDS as [MaintenanceKind, ...MaintenanceKind[]]),
  trigger: z.enum(['Time', 'Usage']),
  interval: z.string().refine((value) => /^\d{1,9}$/.test(latinDigits(value).trim()) && Number(latinDigits(value)) > 0, { error: 'org:maintenance.form.interval_invalid' }),
  estimatedMinutes: z.string().refine((value) => /^\d{1,4}$/.test(latinDigits(value).trim()) && Number(latinDigits(value)) > 0, { error: 'org:maintenance.form.minutes_invalid' }),
  blockingAtDue: z.boolean(),
  isActive: z.boolean(),
});
type PlanForm = z.input<typeof planSchema>;

function PlanDialog({ device, plan, counterPlan, onClose, onSaved }: { device: DeviceView; plan: MaintenancePlanView | null; counterPlan: boolean; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const { control, handleSubmit, formState, watch } = useForm<PlanForm>({
    resolver: zodResolver(planSchema),
    defaultValues: {
      name: plan?.name ?? '',
      kind: plan?.kind ?? 'Service',
      trigger: plan?.trigger ?? (counterPlan ? 'Usage' : 'Time'),
      interval: String(plan ? (plan.trigger === 'Time' ? plan.intervalDays : plan.intervalUnits) : ''),
      estimatedMinutes: String(plan?.estimatedMinutes ?? 120),
      blockingAtDue: plan?.blockingAtDue ?? false,
      isActive: plan?.isActive ?? true,
    },
  });
  const trigger = watch('trigger');
  const save = useMutation({
    mutationFn: (form: PlanForm) => {
      const interval = Number(latinDigits(form.interval));
      const body = {
        name: form.name,
        kind: form.kind,
        trigger: form.trigger,
        intervalDays: form.trigger === 'Time' ? interval : null,
        intervalUnits: form.trigger === 'Usage' ? interval : null,
        estimatedMinutes: Number(latinDigits(form.estimatedMinutes)),
        blockingAtDue: form.blockingAtDue,
        startOn: null,
        startCounter: null,
      };
      return plan
        ? unwrap(tenantApi.PUT('/api/v1/organization/maintenance-plans/{id}', { params: { path: { id: plan.id } }, body: { ...body, isActive: form.isActive, rowVersion: Number(plan.rowVersion) } }))
        : unwrap(tenantApi.POST('/api/v1/organization/devices/{id}/maintenance-plans', { params: { path: { id: device.id } }, body: { ...body, isActive: null, rowVersion: null }, headers: { 'Idempotency-Key': idempotency.current() } }));
    },
    onSettled: (_data, error) => {
      if (!plan) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  return (
    <FormDialog open screenId="SCR-STAFF-936-PLAN" titleKey={plan ? 'org:maintenance.plans.edit_title' : 'org:maintenance.plans.create_title'} onSubmit={() => void handleSubmit((form) => save.mutate(form))()} onClose={onClose} isDirty={formState.isDirty} isSubmitting={save.isPending} submitError={save.error ?? undefined}>
      <TextFieldRHF control={control} name="name" labelKey="org:maintenance.form.name" required fullWidth slotProps={{ htmlInput: { 'data-testid': 'plan-name' } }} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF control={control} name="kind" labelKey="org:maintenance.form.kind" required fullWidth options={MAINTENANCE_KINDS.map((value) => ({ value, labelKey: `org:maintenance_kind.${value}` }))} />
        <SelectRHF
          control={control}
          name="trigger"
          labelKey="org:maintenance.form.trigger"
          required
          fullWidth
          disabled={plan !== null}
          options={[{ value: 'Time', labelKey: 'org:maintenance_trigger.Time' }, ...(counterPlan ? [{ value: 'Usage', labelKey: 'org:maintenance_trigger.Usage' }] : [])]}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="interval"
          labelKey={trigger === 'Time' ? 'org:maintenance.form.interval_days' : 'org:maintenance.form.interval_units'}
          helperKey={trigger === 'Time' ? 'org:maintenance.form.time_help' : 'org:maintenance.form.usage_help'}
          required
          fullWidth
          slotProps={{ htmlInput: { inputMode: 'numeric', 'data-testid': 'plan-interval' } }}
        />
        <TextFieldRHF control={control} name="estimatedMinutes" labelKey="org:maintenance.form.minutes" required fullWidth slotProps={{ htmlInput: { inputMode: 'numeric', 'data-testid': 'plan-minutes' } }} />
      </Stack>
      <CheckboxRHF control={control} name="blockingAtDue" labelKey="org:maintenance.form.blocking" helperKey="org:maintenance.form.blocking_help" />
      {plan && <CheckboxRHF control={control} name="isActive" labelKey="org:maintenance.form.active" />}
      {trigger === 'Usage' && <Alert severity="info">{t('org:maintenance.form.unit_hint', { unit: t(`org:counter_unit.${device.counterUnit}`) })}</Alert>}
    </FormDialog>
  );
}

function BookDialog({ occurrence, onClose, onSaved }: { occurrence: MaintenanceOccurrenceView; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const dates = useForm<{ date: string | null }>({ defaultValues: { date: todayIn(timeZone, 1) } });
  const date = dates.watch('date') ?? todayIn(timeZone, 1);
  const [time, setTime] = useState('08:00');
  const book = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/organization/maintenance-occurrences/{id}:book', { params: { path: { id: occurrence.id } }, body: { from: zonedToUtcIso(date, time, timeZone), to: null } })),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  return (
    <FormDialog open screenId="SCR-STAFF-936-BOOK" titleKey="org:maintenance.book_title" onSubmit={() => book.mutate()} onClose={onClose} isDirty isSubmitting={book.isPending} submitError={book.error ?? undefined}>
      <Typography variant="body2">{t('org:maintenance.book_hint', { plan: occurrence.planName })}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <JalaliDatePickerRHF control={dates.control} name="date" labelKey="org:maintenance.form.book_date" required />
        <TextField type="time" fullWidth label={t('org:maintenance.form.book_time')} value={time} onChange={(event) => setTime(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'book-time' } }} />
      </Stack>
    </FormDialog>
  );
}

const recordSchema = z.object({
  kind: z.enum(MAINTENANCE_KINDS as [MaintenanceKind, ...MaintenanceKind[]]),
  performedOn: z.string({ error: 'common:validation.required' }).min(1, { error: 'common:validation.required' }),
  performer: z.enum(['Internal', 'Supplier']),
  supplierId: z.string(),
  description: zRequiredText(2000),
  laborCost: z.number().int().nonnegative().nullable(),
  result: z.enum(RESULTS as [MaintenanceResult, ...MaintenanceResult[]]),
  certificateNumber: z.string().max(100),
  certificateAuthority: z.string().max(200),
  certificateExpiresOn: z.string().nullable(),
});
type RecordForm = z.input<typeof recordSchema>;

function RecordDialog({ device, occurrence, onClose, onSaved }: { device: DeviceView; occurrence: MaintenanceOccurrenceView | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const idempotency = useIdempotencyKey();
  const suppliers = useTechnicalSuppliers();
  const [outcome, setOutcome] = useState<{ workOrder: boolean; certificate: boolean } | null>(null);
  const { control, handleSubmit, formState, watch } = useForm<RecordForm>({
    resolver: zodResolver(recordSchema),
    defaultValues: {
      kind: 'Service',
      performedOn: todayIn(timeZone),
      performer: 'Internal',
      supplierId: '',
      description: '',
      laborCost: null,
      result: 'Pass',
      certificateNumber: '',
      certificateAuthority: '',
      certificateExpiresOn: null,
    },
  });
  const kind = watch('kind');
  const performer = watch('performer');
  const result = watch('result');
  const calibrationPass = kind === 'Calibration' && result !== 'Fail';
  const save = useMutation({
    mutationFn: (form: RecordForm) =>
      unwrap(
        tenantApi.POST('/api/v1/organization/maintenance-records', {
          body: {
            deviceId: occurrence ? null : device.id,
            occurrenceId: occurrence?.id ?? null,
            kind: occurrence ? null : form.kind,
            performedOn: form.performedOn,
            performer: form.performer,
            performerMembershipId: null,
            supplierId: form.performer === 'Supplier' ? form.supplierId || null : null,
            description: form.description,
            laborCost: { amount: form.laborCost ?? 0, currency: 'IRR' },
            result: form.result,
            reportFileId: null,
            certificate:
              calibrationPass && form.certificateNumber.trim() && form.certificateExpiresOn
                ? { number: form.certificateNumber.trim(), authority: form.certificateAuthority.trim() || '—', expiresOn: form.certificateExpiresOn, fileId: null }
                : null,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (response) => {
      onSaved();
      if (response.workOrderId || response.certificateId) {
        setOutcome({ workOrder: Boolean(response.workOrderId), certificate: Boolean(response.certificateId) });
      } else {
        onClose();
      }
    },
  });
  if (outcome) {
    return (
      <FormDialog open screenId="SCR-STAFF-936-RECORD" titleKey="org:maintenance.record_title" onSubmit={onClose} onClose={onClose} isDirty={false} isSubmitting={false} submitKey="common:actions.close">
        {outcome.workOrder && (
          <Alert severity="error" data-testid="record-work-order">
            {t('org:maintenance.failed_calibration')}
          </Alert>
        )}
        {outcome.certificate && (
          <Alert severity="success" data-testid="record-certificate">
            {t('org:maintenance.certificate_updated')}
          </Alert>
        )}
      </FormDialog>
    );
  }
  return (
    <FormDialog open screenId="SCR-STAFF-936-RECORD" titleKey="org:maintenance.record_title" onSubmit={() => void handleSubmit((form) => save.mutate(form))()} onClose={onClose} isDirty={formState.isDirty} isSubmitting={save.isPending} submitError={save.error ?? undefined} maxWidth="md">
      {occurrence ? (
        <Typography variant="body2">{t('org:maintenance.record_for', { plan: occurrence.planName })}</Typography>
      ) : (
        <SelectRHF control={control} name="kind" labelKey="org:maintenance.form.kind" required fullWidth options={MAINTENANCE_KINDS.map((value) => ({ value, labelKey: `org:maintenance_kind.${value}` }))} />
      )}
      {occurrence && Number(occurrence.partsCost.amount) > 0 && (
        <Alert severity="info" data-testid="record-parts">
          {t('org:maintenance.parts_issued')} <Money amountMinor={Number(occurrence.partsCost.amount)} />
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <JalaliDatePickerRHF control={control} name="performedOn" labelKey="org:maintenance.form.performed_on" required />
        <SelectRHF control={control} name="performer" labelKey="org:maintenance.form.performer" required fullWidth options={[{ value: 'Internal', labelKey: 'org:performer.Internal' }, { value: 'Supplier', labelKey: 'org:performer.Supplier' }]} />
      </Stack>
      {performer === 'Supplier' && suppliers.allowed && (
        <SelectRHF control={control} name="supplierId" labelKey="org:maintenance.form.supplier" fullWidth options={[{ value: '', labelKey: 'org:contracts.form.supplier_none' }, ...suppliers.options]} />
      )}
      <TextFieldRHF control={control} name="description" labelKey="org:maintenance.form.description" required fullWidth multiline minRows={2} slotProps={{ htmlInput: { 'data-testid': 'record-description' } }} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <MoneyFieldRHF control={control} name="laborCost" labelKey="org:maintenance.form.labor_cost" helperKey="org:maintenance.form.labor_help" fullWidth />
        <SelectRHF control={control} name="result" labelKey="org:maintenance.form.result" required fullWidth options={RESULTS.map((value) => ({ value, labelKey: `org:maintenance_result.${value}` }))} />
      </Stack>
      {kind === 'Calibration' && result === 'Fail' && <Alert severity="warning">{t('org:maintenance.form.fail_warning')}</Alert>}
      {calibrationPass && !occurrence && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextFieldRHF control={control} name="certificateNumber" labelKey="org:maintenance.form.certificate_number" fullWidth slotProps={{ htmlInput: { dir: 'ltr' } }} />
          <TextFieldRHF control={control} name="certificateAuthority" labelKey="org:maintenance.form.certificate_authority" fullWidth />
          <JalaliDatePickerRHF control={control} name="certificateExpiresOn" labelKey="org:maintenance.form.certificate_expires" />
        </Stack>
      )}
    </FormDialog>
  );
}

// ───────────── بهره‌وری (US-ORG-043) ─────────────

export function UtilizationTab({ device }: { device: DeviceView }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const [days, setDays] = useState(30);
  const range = lastDays(days);
  const report = useQuery({
    queryKey: [...maintenanceKeys.device(session?.tenantId, device.id, 'utilization'), days],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/devices/{id}/utilization', { params: { path: { id: device.id }, query: { from: range.from, to: range.to } } })),
  });
  const data = report.data;
  const hours = (minutes: number | string) => faDigits((Number(minutes) / 60).toFixed(1).replace(/\.0$/, ''));
  return (
    <Stack spacing={2}>
      <TextField select size="small" label={t('org:utilization.period')} value={days} onChange={(event) => setDays(Number(event.target.value))} sx={{ maxWidth: 220 }} slotProps={{ select: { native: true }, htmlInput: { 'data-testid': 'utilization-period' } }}>
        {[7, 30, 90, 365].map((value) => (
          <option key={value} value={value}>
            {t('org:utilization.last_days', { n: faDigits(value) })}
          </option>
        ))}
      </TextField>
      {data && (
        <Rows
          label={t('org:utilization.title')}
          rows={[
            ['org:utilization.available', t('org:utilization.hours', { n: hours(data.availableMinutes) })],
            ['org:utilization.booked', t('org:utilization.hours', { n: hours(data.bookedMinutes) })],
            ['org:utilization.blocked', t('org:utilization.hours', { n: hours(data.blockedMinutes) })],
            ['org:utilization.downtime', t('org:utilization.hours', { n: hours(data.downtimeMinutes) })],
            ['org:utilization.rate', <span key="rate" data-testid="utilization-rate">{t('org:counter.percent', { percent: percentOf(data.utilizationBasisPoints) })}</span>],
            ['org:utilization.usage', <span key="usage" data-testid="utilization-usage">{`${faDigits(Number(data.counterUsage))} ${t(`org:counter_unit.${data.counterUnit}`)}`}</span>],
            ['org:utilization.sessions', faDigits(Number(data.sessions))],
            ['org:utilization.usage_cost', <Money key="usage-cost" amountMinor={Number(data.usageCost.amount)} />],
            ['org:utilization.maintenance_cost', <span key="maintenance" data-testid="utilization-maintenance-cost"><Money amountMinor={Number(data.maintenanceCost.amount)} /></span>],
            ['org:utilization.revenue', <Money key="revenue" amountMinor={Number(data.revenue.amount)} />],
            [
              'org:utilization.current_rate',
              data.currentRate ? (
                <span key="current" data-testid="utilization-current-rate">
                  <Money amountMinor={Number(data.currentRate.rate.amount)} /> {t(`org:cost_basis.${data.currentRate.basis}`, { unit: t(`org:counter_unit.${data.counterUnit}`) })}
                </span>
              ) : (
                '—'
              ),
            ],
            [
              'org:utilization.maintenance_per_unit',
              data.maintenanceCostPerUnit ? (
                <span key="per-unit" data-testid="utilization-cost-per-unit">
                  <Money amountMinor={Number(data.maintenanceCostPerUnit.amount)} />
                </span>
              ) : (
                '—'
              ),
            ],
          ]}
        />
      )}
      {report.isPending && <Typography color="text.secondary">{t('common:states.loading')}</Typography>}
    </Stack>
  );
}
